import { DownOutlined } from "@ant-design/icons";
import { Button, Dropdown, Menu, Popconfirm, message } from "antd";
import ExportList from "components/ExportList";
import logger from "logger";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { RFQState } from "scenes/RFQs";
import exportRecords from "scenes/utils/exportRecords";
import { RFQService } from "services";
import { RFQ } from "types";
import TableSettings from "utils/components/TableSettings";

import UserContext from "../../../../UserContext";
import FieldsList from "../../../CustomFields/CustomFieldsList";
import RFQFiltersForm from "./RFQFiltersForm";

interface RFQHeaderProps {
  parentState: RFQState;
  updateParentState: (changes: Partial<RFQState>) => void;
}

const RFQHeader = ({ parentState, updateParentState }: RFQHeaderProps) => {
  const [t] = useTranslation();
  const { hasPermission }: any = useContext(UserContext);
  const [state, setState] = useState({
    customFieldModalVisibile: false,
    exportListVisible: false,
    tableSettingsVisible: false,
  });
  const { filters, selectedRowKeys } = parentState;

  const updateState = (changes: Partial<typeof state>) =>
    setState((old) => ({ ...old, ...changes }));

  const bulkDeleteRFQs = () => {
    updateParentState({ isLoading: true });
    message.loading({
      content: `${t("bulkActions.deleting")} ${selectedRowKeys.length} ${t(
        "bulkActions.records",
      )}`,
      key: "deletingRFQs",
      duration: 0,
    });
    RFQService.remove(null, { query: { _id: { $in: selectedRowKeys } } }).then(
      (res: RFQ[]) => {
        const deleted = res.length;
        message.success({
          content: `${t("bulkActions.deleted")} ${deleted} ${t(
            "bulkActions.selectedRecords",
          )}`,
          key: "deletingRFQs",
        });
        updateParentState({ selectedRowKeys: [], isLoading: false });
        window.location.reload();
      },
      (e: Error) => {
        message.error({
          content: t("tasks.bulkDeleteError"),
          key: "deletingRFQs",
        });
        logger.error("Error in bulk deleting RFQs: ", e);
        updateParentState({ isLoading: false });
      },
    );
  };

  return (
    <div className="tw-mb-6">
      <div className="tw-flex tw-items-center tw-mb-5">
        <div className="s-page-title tw-mr-auto">{t("requests.pageTitle")}</div>
        {selectedRowKeys.length > 0 ? (
          <>
            <Button
              onClick={() => updateParentState({ selectedRowKeys: [] })}
              type="dashed"
            >
              {t("global.clearSelection")}
            </Button>
            <Popconfirm
              title={t("global.deleteMsg")}
              okText={t("global.delete")}
              cancelText={t("global.cancel")}
              onConfirm={bulkDeleteRFQs}
            >
              <Button danger className="tw-mx-2" ghost>
                {t("bulkActions.delete")} {selectedRowKeys.length}{" "}
                {t("bulkActions.selectedItems")}
              </Button>
            </Popconfirm>
            <Button
              onClick={() =>
                exportRecords(
                  {
                    serviceName: "rfqs",
                    exportType: "selectedRecords",
                    ids: selectedRowKeys,
                  },
                  () => updateParentState({ selectedRowKeys: [] }),
                )
              }
              type="default"
              className="tw-px-5"
            >
              {t("exports.export")}{" "}
              {selectedRowKeys.length > 0 ? selectedRowKeys.length : ""}{" "}
              {t("exports.selectedItems")}
            </Button>
          </>
        ) : null}
        {hasPermission("canManageAllRFQs") ? (
          <Button
            className="tw-uppercase s-semibold tw-mx-2"
            type="primary"
            onClick={() =>
              updateParentState({ isEditing: true, editedRecord: {} as RFQ })
            }
            ghost
          >
            {t("requests.create")}
          </Button>
        ) : null}
        <Dropdown
          overlay={
            <Menu>
              {hasPermission("canManageCustomFields") && (
                <Menu.Item
                  onClick={() =>
                    updateState({ customFieldModalVisibile: true })
                  }
                >
                  {t("fields.addCustomFields")}
                </Menu.Item>
              )}
              <Menu.Item
                onClick={() =>
                  exportRecords({
                    serviceName: "rfqs",
                    exportType: "allRecords",
                  })
                }
              >
                {t("jobExports.exportAll")}
              </Menu.Item>
              <Menu.Item
                onClick={() => updateState({ exportListVisible: true })}
              >
                {t("exports.downloadExports")}
              </Menu.Item>
              <Menu.Item
                onClick={() => updateState({ tableSettingsVisible: true })}
              >
                {t("tableSettings.changeLayout")}
              </Menu.Item>
            </Menu>
          }
          placement="bottomLeft"
        >
          <Button className="tw-inline-flex tw-items-center">
            {t("global.actions")}
            <DownOutlined />
          </Button>
        </Dropdown>
      </div>
      <div className="tw-flex tw-items-center tw-w-full tw-mt-2">
        <div className="tw-flex-1">
          <RFQFiltersForm
            applyFilters={(filters) => updateParentState({ filters })}
            filters={filters}
          />
        </div>
      </div>

      <FieldsList
        form="rfq"
        visible={state.customFieldModalVisibile}
        handleClose={() => updateState({ customFieldModalVisibile: false })}
      />
      <TableSettings
        visible={state.tableSettingsVisible}
        table="rfq"
        handleClose={() => updateState({ tableSettingsVisible: false })}
      />
      <ExportList
        serviceName="rfqs"
        visible={state.exportListVisible}
        toggleVisible={() => updateState({ exportListVisible: false })}
      />
    </div>
  );
};

export default RFQHeader;
